import * as React from "react";
import BlogPostTemplate from "@/components/blog/BlogPostTemplate";
import { LocalizedLink } from "@/components/LocalizedLink";

const link = "text-primary underline underline-offset-4";

const InodoroInteligenteGuia = () => {
  return (
    <BlogPostTemplate
      title="Inodoro inteligente: guía para elegirlo en España (vivienda, hotel y promoción)"
      metaTitle="Inodoro inteligente: guía de compra en España | Aureum VII"
      metaDescription="Qué es un inodoro inteligente o japonés, qué instalación necesita en España (agua, electricidad, desagüe) y cómo especificarlo en viviendas, hoteles y promociones."
      keywords="inodoro inteligente, inodoro japonés, wc inteligente, inodoro inteligente precio, inodoro suspendido inteligente, sanitarios inteligentes España"
      author="Aureum VII Trading"
      date="October 8, 2026"
      readTime="9 min de lectura"
      category="Guías"
      tags={["Inodoro inteligente", "España", "Guía de compra", "B2B"]}
      featuredImage="/lovable-uploads/7a774286-73c5-43d4-b368-b47766cb1465.png"
      featuredImageAlt="Baño contemporáneo con inodoro inteligente"
      relatedProducts={["rem7", "aria", "luxa"]}
      quickAnswer={
        <>
          Un inodoro inteligente (también llamado inodoro japonés o WC inteligente) integra
          función bidé con agua templada, asiento calefactado, secado y apertura automática
          en el propio sanitario. En España necesita tres cosas: toma de agua fría con llave
          de corte, un enchufe dedicado con protección diferencial instalado fuera de las
          zonas de baño que limita la normativa eléctrica, y un desagüe compatible con la
          salida del modelo. Para viviendas de lujo, hoteles y promociones, conviene fijar
          estos requisitos en el proyecto antes de la primera fase de instalaciones.
        </>
      }
      faqs={[
        {
          question: "¿Qué diferencia hay entre un inodoro inteligente y un inodoro japonés?",
          answer:
            "En la práctica se usan como sinónimos. «Inodoro japonés» hace referencia al origen del concepto de asiento con bidé integrado; «inodoro inteligente» describe el sanitario completo con funciones electrónicas como bidé, asiento calefactado, secado, desodorización y apertura automática.",
        },
        {
          question: "¿Necesita instalación eléctrica especial?",
          answer:
            "Necesita una toma de corriente de 220–240 V dedicada y protegida por diferencial, situada fuera de las zonas del baño restringidas por el Reglamento Electrotécnico de Baja Tensión. La debe ejecutar un instalador autorizado.",
        },
        {
          question: "¿Qué presión de agua necesita?",
          answer:
            "Depende del modelo. REM7 funciona entre 0,07 y 0,75 MPa y ARIA desde 0,05 MPa. En edificios en altura conviene comprobar la presión en el punto de consumo más desfavorable, no solo en la acometida.",
        },
        {
          question: "¿Cuánto cuesta un inodoro inteligente de Aureum VII?",
          answer:
            "Oferta especial: 600 € por unidad para REM7. Para proyectos con varias unidades, los precios y condiciones se confirman por escrito en cada presupuesto.",
        },
        {
          question: "¿Hay modelos suspendidos?",
          answer:
            "Sí. ARIA es un modelo suspendido (360 × 540 × 340 mm) que requiere bastidor empotrado y profundidad de tabique suficiente, algo que debe coordinarse con la tabiquería del proyecto.",
        },
      ]}
    >
      <div className="space-y-6">
        <p className="text-lg">
          Las búsquedas de <strong>inodoro inteligente</strong> e <strong>inodoro japonés</strong>{" "}
          crecen en España, sobre todo en reformas de alto nivel y en hoteles boutique. Esta guía
          explica qué ofrece realmente un WC inteligente, qué exige la instalación y cómo
          evitar sorpresas en obra, con los datos publicados de nuestra serie insignia{" "}
          <LocalizedLink to="/products/rem7" className={link}>REM7</LocalizedLink>.
        </p>

        <h2 className="text-2xl font-semibold mt-8 mb-4">Qué es un inodoro inteligente</h2>
        <p>
          Es un sanitario que integra funciones electrónicas de higiene y confort. Las más
          habituales son:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Bidé integrado con presión y temperatura del agua regulables.</li>
          <li>Asiento calefactado y secado con aire templado.</li>
          <li>Apertura y cierre automático de la tapa, y cierre amortiguado.</li>
          <li>Desodorización y autolimpieza de la boquilla.</li>
          <li>Luz nocturna y control mediante mando o panel.</li>
        </ul>
        <p>
          No todos los modelos incluyen todas las funciones: compare siempre la ficha concreta
          de cada producto.
        </p>

        <h2 className="text-2xl font-semibold mt-8 mb-4">Requisitos de instalación en España</h2>
        <h3 className="text-xl font-semibold mt-6 mb-3">Agua</h3>
        <p>
          Toma de agua fría con llave de corte accesible. La presión de trabajo varía por
          modelo: 0,07–0,75 MPa en REM7, desde 0,05 MPa en{" "}
          <LocalizedLink to="/products/aria" className={link}>ARIA</LocalizedLink> y
          0,08–0,75 MPa en{" "}
          <LocalizedLink to="/products/luxa" className={link}>LUXA</LocalizedLink>.
        </p>
        <h3 className="text-xl font-semibold mt-6 mb-3">Electricidad</h3>
        <p>
          Enchufe dedicado de 220–240 V con protección diferencial, situado fuera de las zonas
          del baño restringidas por la normativa de baja tensión. REM7 tiene un consumo máximo
          de 1200 W y protección IPX4. La instalación debe hacerla un profesional autorizado.
        </p>
        <h3 className="text-xl font-semibold mt-6 mb-3">Desagüe</h3>
        <p>
          REM7 admite salida a suelo (S) de 300±400 mm, personalizable entre 200 y 450 mm, o
          salida a pared (P) de unos 100 mm. En reformas, compruebe la posición y pendiente del
          desagüe existente antes de elegir modelo.
        </p>

        <h2 className="text-2xl font-semibold mt-8 mb-4">Suspendido o de suelo</h2>
        <p>
          Un <strong>inodoro suspendido inteligente</strong> como ARIA despeja el suelo y
          facilita la limpieza, pero necesita bastidor y profundidad de tabique. Un modelo de
          suelo como REM7 (715 × 625 × 465 mm, 35 kg) simplifica la sustitución en reformas.
          La elección depende más del baño que del producto.
        </p>

        <h2 className="text-2xl font-semibold mt-8 mb-4">Para hoteles y promociones</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Incluya en el pliego modelo, dimensiones, salida, presión y potencia por unidad.</li>
          <li>Deje como partidas de replanteo la presión real por montante y la capacidad del cuadro eléctrico.</li>
          <li>Unifique modelo por tipología de baño para simplificar repuestos y mantenimiento.</li>
        </ul>
        <p>
          Más detalle técnico en nuestra{" "}
          <LocalizedLink to="/blog/smart-toilet-spec-sheet-guide" className={link}>
            guía de especificación (en inglés)
          </LocalizedLink>{" "}
          y en la página de{" "}
          <LocalizedLink to="/markets/spain" className={link}>sanitarios inteligentes en España</LocalizedLink>.
        </p>

        <div className="bg-muted p-6 rounded-lg mt-8">
          <h3 className="text-xl font-semibold mb-3">¿Proyecto con varias unidades?</h3>
          <p className="mb-4">
            Nuestro equipo de{" "}
            <LocalizedLink to="/business" className={link}>proyectos B2B</LocalizedLink>{" "}
            prepara presupuestos y documentación técnica para arquitectos, promotores y hoteles.
          </p>
          <LocalizedLink
            to="/contact?intent=trade#enquiry-form"
            className="inline-flex items-center rounded-md bg-primary px-5 py-3 text-primary-foreground font-medium"
          >
            Solicitar precio profesional
          </LocalizedLink>
          <p className="mt-4 text-sm">info@aureum7trading.com · WhatsApp +34 642 278 939</p>
        </div>
      </div>
    </BlogPostTemplate>
  );
};

export default InodoroInteligenteGuia;
